<inactivity-warning-modal>
    <dialog id="inactivity-modal" class="modal" aria-labelledby="inactivity-modal-title">
        <div class="modal-box max-w-md">
            <div class="text-center mb-4">
                <div class="text-warning text-6xl mb-4">
                    <i class="icon-metalark-clock"></i>
                </div>
                <h3 id="inactivity-modal-title" class="font-bold text-xl mb-2">{locale?.t('inactivity_title')}</h3>
            </div>

            <div class="text-center py-4">
                <p class="text-base mb-4">{locale?.t('inactivity_expires_in')}:</p>
                <div class="countdown-display text-5xl font-mono font-bold text-error mb-4">
                    {formatTime(state.secondsRemaining)}
                </div>
                <p class="text-sm text-base-content/80">
                    {locale?.t('inactivity_action_hint')}
                </p>
            </div>

            <div class="modal-action justify-center">
                <button class="btn btn-primary btn-lg gap-2" onclick="{confirmPresence}">
                    <i class="icon-metalark-check"></i>
                    {locale?.t('inactivity_im_here')}
                </button>
            </div>
        </div>
    </dialog>

    <style>
        #inactivity-modal::backdrop {
            background-color: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(2px);
        }

        .countdown-display {
            letter-spacing: 0.1em;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        /* Pulse animation for countdown */
        @keyframes pulse-warning {
            0%, 100% {
                opacity: 1;
            }
            50% {
                opacity: 0.7;
            }
        }

        .countdown-display {
            animation: pulse-warning 2s ease-in-out infinite;
        }
    </style>

    <script>
        import { getSessionActivityService, getSessionRenewalService } from '../../services/service-loader';

        export default {
            includeTools: false,

            state: {
                visible: false,
                secondsRemaining: 0,
                expiresAt: null
            },

            async onMounted() {
                // Listen for activity - any activity dismisses modal and triggers renewal
                const sessionActivityService = await getSessionActivityService();
                this.unsubscribeActivity = sessionActivityService.subscribe(() => {
                    if (this.state.visible) {
                        console.log('✅ Activity detected during inactivity warning - renewing session')
                        this.confirmPresence()
                    }
                })
            },

            onUnmounted() {
                if (this.unsubscribeActivity) {
                    this.unsubscribeActivity()
                }
                this.stopCountdown()
            },

            /**
             * Show the inactivity modal
             * @param {number} timeRemaining - Milliseconds until session expires
             */
            show(timeRemaining) {
                const expiresAt = Date.now() + timeRemaining
                this.update({
                    visible: true,
                    expiresAt: expiresAt,
                    secondsRemaining: Math.floor(timeRemaining / 1000)
                })
                
                // Show the modal
                const modal = document.getElementById('inactivity-modal')
                if (modal) {
                    modal.showModal()
                    console.log('⚠️ Inactivity modal shown:', {
                        secondsRemaining: this.state.secondsRemaining,
                        expiresAt: new Date(expiresAt).toISOString()
                    })
                }
                
                this.startCountdown()
            },

            /**
             * Hide the modal
             */
            hide() {
                this.update({ visible: false })
                this.stopCountdown()
                
                const modal = document.getElementById('inactivity-modal')
                if (modal) {
                    modal.close()
                }
            },

            /**
             * Start countdown timer
             */
            startCountdown() {
                this.stopCountdown()
                
                this.countdownTimer = setInterval(async () => {
                    if (!this.state.expiresAt) return

                    const remaining = this.state.expiresAt - Date.now()

                    if (remaining <= 0) {
                        console.log('⏰ Session expired during inactivity warning')
                        this.hide()

                        // Trigger the session-expired event through sessionRenewalService
                        // This allows session-manager to handle Redux clearing and other cleanup
                        const sessionRenewalService = await getSessionRenewalService();
                        sessionRenewalService.emit('session-expired')
                        console.log('✅ Emitted session-expired event for session-manager to handle')
                    } else {
                        this.update({
                            secondsRemaining: Math.floor(remaining / 1000)
                        })
                    }
                }, 1000)
            },

            /**
             * Stop countdown timer
             */
            stopCountdown() {
                if (this.countdownTimer) {
                    clearInterval(this.countdownTimer)
                    this.countdownTimer = null
                }
            },

            /**
             * User confirmed presence - hide modal and trigger renewal
             */
            async confirmPresence() {
                console.log('✅ User confirmed presence')
                this.hide()

                // Force record activity to update timestamp
                const sessionActivityService = await getSessionActivityService();
                sessionActivityService.forceRecordActivity()

                // Trigger session renewal
                const sessionRenewalService = await getSessionRenewalService();
                sessionRenewalService.renewToken()
            },

            /**
             * Format seconds as MM:SS
             * @param {number} seconds - Seconds to format
             * @returns {string} Formatted time string
             */
            formatTime(seconds) {
                if (seconds < 0) return '0:00'
                
                const minutes = Math.floor(seconds / 60)
                const secs = seconds % 60
                
                return `${minutes}:${String(secs).padStart(2, '0')}`
            }
        }
    </script>
</inactivity-warning-modal>